import { List, Map } from "immutable";
import { observable, Observable } from "micro-observables";
import { Signal } from "micro-signals";
import { Page, PaginationOptions } from "@utils/page";
import { PaginatedData } from "@core/paginatedData";
import { MappedStore, SimpleMappedStore } from "./mappedStore";
import { Store } from "./store";

export class ContextStore<T extends U, U extends { id: string }, S extends string = string> implements MappedStore<
	T,
	S
> {
	private readonly _mappedStore: MappedStore<T, S>;
	private readonly _mappedItemsById = observable(Map<S, Map<string, T>>());
	onChange = new Signal<{ id: S; data: PaginatedData<T> | null }>();

	constructor(
		private readonly _store: Store<U>,
		fetchList: (id: S, page: PaginationOptions) => Promise<Page<T>>,
		private readonly _fetchOne: (context: S, id: string) => Promise<T>,
		private readonly removeDetails: (v: T) => U
	) {
		this._mappedStore = new SimpleMappedStore<T, S>(fetchList);
		this._mappedStore.onChange.add(({ data, id }) => {
			if (data === null) {
				return;
			}

			this._store.merge(data.content.map(removeDetails));
			this.onChange.dispatch({ data, id });
		});
	}

	updateContent(id: S, updater: (content: List<T>) => List<T>): void {
		this._mappedStore.updateContent(id, updater);
	}

	async fetchOne(context: S, id: string): Promise<void> {
		const result = await this._fetchOne(context, id);
		this._mappedItemsById.update((items) => items.setIn([context, id], result));
		this._store.save(this.removeDetails(result));
	}

	fetch(id: S): Promise<void> {
		return this._mappedStore.fetch(id);
	}

	fetchMore(id: S): Promise<void> {
		return this._mappedStore.fetchMore(id);
	}

	getObservable(id: S): Observable<PaginatedData<T> | null> {
		return Observable.from(this._store.itemsById, this._mappedStore.getObservable(id)).transform(
			([items, detailedPaginatedItems]) =>
				detailedPaginatedItems
					? {
							...detailedPaginatedItems,
							content: detailedPaginatedItems.content.map((detailedItem) => {
								return { ...detailedItem, ...items.get(detailedItem.id) };
							}),
						}
					: null
		);
	}

	getOne(context: S, id: string): Observable<T | null> {
		return Observable.from(this._store.itemsById, this._mappedItemsById).transform(([items, detailedItems]) => {
			const detailedItem = detailedItems.get(context)?.get(id) ?? null;
			const item = items.get(id) ?? null;

			if (!detailedItem || !item) {
				return null;
			}
			return { ...detailedItem, ...item };
		});
	}
}
